import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaFile, useMediaStore } from '../store/media';

interface MediaPickerProps {
  projectId: string;
  selectedMediaIds: string[];
  onSelectionChange: (mediaIds: string[], mediaUrls: string[]) => void;
  maxSelection?: number;
}

// Single-Select Modal für Creator Studio
interface MediaPickerModalProps {
  projectId: string;
  onSelect: (media: MediaFile) => void;
  onClose: () => void;
  filterType?: 'image' | 'video' | 'audio' | 'all';
}

export function MediaPickerModal({
  projectId,
  onSelect,
  onClose,
  filterType = 'all',
}: MediaPickerModalProps) {
  const { t } = useTranslation(['media', 'common']);
  const { media, isLoading, fetchMedia } = useMediaStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (projectId) {
      fetchMedia(projectId);
    }
  }, [projectId, fetchMedia]);

  const filteredMedia = media.filter((m) => {
    // Filter by type
    if (filterType !== 'all') {
      if (filterType === 'image' && !m.mime_type.startsWith('image/')) return false;
      if (filterType === 'video' && !m.mime_type.startsWith('video/')) return false;
      if (filterType === 'audio' && !m.mime_type.startsWith('audio/')) return false;
    }
    // Filter by search
    if (searchQuery && !m.original_name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const isImage = (mimeType: string) => mimeType.startsWith('image/');
  const isVideo = (mimeType: string) => mimeType.startsWith('video/');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('media:picker.selectImage')}</h3>
            <p className="text-sm text-gray-500">{t('media:picker.availableCount', { count: filteredMedia.length })}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('media:picker.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="font-medium">{t('media:picker.noMediaFound')}</p>
              <p className="text-sm">{t('media:picker.uploadFirst')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {filteredMedia.map((m) => (
                <button
                  key={m.id}
                  onClick={() => onSelect(m)}
                  className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 hover:ring-2 hover:ring-blue-200 transition-all group"
                >
                  {isImage(m.mime_type) ? (
                    <img src={m.url} alt={m.original_name} className="w-full h-full object-cover" />
                  ) : isVideo(m.mime_type) ? (
                    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                      </svg>
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-2 shadow-lg transition-opacity">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>

                  {/* File name */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs p-2 pt-4 truncate">
                    {m.original_name}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MediaPicker({
  projectId,
  selectedMediaIds,
  onSelectionChange,
  maxSelection = 10,
}: MediaPickerProps) {
  const { t } = useTranslation(['media', 'common']);
  const { media, isLoading, fetchMedia } = useMediaStore();
  const [isOpen, setIsOpen] = useState(false);
  const [localSelection, setLocalSelection] = useState<Set<string>>(new Set(selectedMediaIds));

  useEffect(() => {
    if (isOpen && projectId) {
      fetchMedia(projectId);
    }
  }, [isOpen, projectId, fetchMedia]);

  useEffect(() => {
    setLocalSelection(new Set(selectedMediaIds));
  }, [selectedMediaIds]);

  const handleToggleMedia = (mediaFile: MediaFile) => {
    const newSelection = new Set(localSelection);
    if (newSelection.has(mediaFile.id)) {
      newSelection.delete(mediaFile.id);
    } else if (newSelection.size < maxSelection) {
      newSelection.add(mediaFile.id);
    }
    setLocalSelection(newSelection);
  };

  const handleConfirm = () => {
    const selectedMedia = media.filter((m) => localSelection.has(m.id));
    const mediaIds = selectedMedia.map((m) => m.id);
    const mediaUrls = selectedMedia.map((m) => m.url);
    onSelectionChange(mediaIds, mediaUrls);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setLocalSelection(new Set(selectedMediaIds));
    setIsOpen(false);
  };

  const selectedMedia = media.filter((m) => selectedMediaIds.includes(m.id));

  const isImage = (mimeType: string) => mimeType.startsWith('image/');
  const isVideo = (mimeType: string) => mimeType.startsWith('video/');

  return (
    <div>
      {/* Selected Media Preview */}
      <div className="mb-2">
        {selectedMedia.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedMedia.map((m) => (
              <div key={m.id} className="relative group">
                {isImage(m.mime_type) ? (
                  <img
                    src={m.url}
                    alt={m.original_name}
                    className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                  />
                ) : isVideo(m.mime_type) ? (
                  <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                    </svg>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const newIds = selectedMediaIds.filter((id) => id !== m.id);
                    const newUrls = selectedMedia.filter((sm) => sm.id !== m.id).map((sm) => sm.url);
                    onSelectionChange(newIds, newUrls);
                  }}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t('media:picker.noMediaSelected')}</p>
        )}
      </div>

      {/* Open Picker Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        {t('media:picker.fromLibrary')}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">{t('media:picker.selectMedia')}</h3>
                <p className="text-sm text-gray-500">
                  {t('media:picker.selectedOfMax', { selected: localSelection.size, max: maxSelection })}
                </p>
              </div>
              <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-48">
                  <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : media.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                  <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>{t('media:picker.noMedia')}</p>
                  <p className="text-sm">{t('media:picker.uploadFirst')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                  {media.map((m) => {
                    const isSelected = localSelection.has(m.id);
                    const canSelect = isSelected || localSelection.size < maxSelection;

                    return (
                      <div
                        key={m.id}
                        onClick={() => canSelect && handleToggleMedia(m)}
                        className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          isSelected
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : canSelect
                            ? 'border-transparent hover:border-gray-300'
                            : 'border-transparent opacity-50 cursor-not-allowed'
                        }`}
                      >
                        {isImage(m.mime_type) ? (
                          <img src={m.url} alt={m.original_name} className="w-full h-full object-cover" />
                        ) : isVideo(m.mime_type) ? (
                          <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                            </svg>
                          </div>
                        )}

                        {/* Selection Indicator */}
                        {isSelected && (
                          <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}

                        {/* File name tooltip */}
                        <div className="absolute inset-x-0 bottom-0 bg-black bg-opacity-60 text-white text-xs p-1 truncate">
                          {m.original_name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('common:actions.apply')} ({localSelection.size})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
